export const BUDGET_MONTHS = [
  { key: 'january', label: 'Jan' },
  { key: 'february', label: 'Feb' },
  { key: 'march', label: 'Mar' },
  { key: 'april', label: 'Apr' },
  { key: 'may', label: 'May' },
  { key: 'june', label: 'Jun' },
  { key: 'july', label: 'Jul' },
  { key: 'august', label: 'Aug' },
  { key: 'september', label: 'Sep' },
  { key: 'october', label: 'Oct' },
  { key: 'november', label: 'Nov' },
  { key: 'december', label: 'Dec' },
];

// a budget term decides how many columns the budget is captured in. term_id is
// the position of the period inside the financial year (Jan = 1, Q1 = 1 ...).
const BUDGET_TERM_LABELS = {
  1: BUDGET_MONTHS.map(month => month.label), // Monthly
  2: ['Q1', 'Q2', 'Q3', 'Q4'], // Quarterly
  3: ['H1', 'H2'], // Half-Yearly
  4: ['Full Year'], // Yearly
};

export function getBudgetPeriods(budgetTermId) {
  const labels = BUDGET_TERM_LABELS[Number(budgetTermId)] || BUDGET_TERM_LABELS[1];
  return labels.map((label, index) => ({ term_id: index + 1, label }));
}

// budget lines always come back in twelve month columns, whatever the term the
// budget was captured in, so the months are rolled back up into that term.
const MONTHS_PER_TERM = { 1: 1, 2: 3, 3: 6, 4: 12 };
// Detail responses use different field names for each budget term. Keep the
// values in a canonical twelve-month array by placing a period total at the
// first month in that period; the existing collapse helper then presents the
// correct monthly, quarterly, half-yearly, or yearly columns.
const TERM_PERIOD_FIELDS = {
  1: BUDGET_MONTHS.map((month, index) => ({ key: month.key, monthIndex: index })),
  2: [
    { key: 'january_to_march', monthIndex: 0 },
    { key: 'april_to_june', monthIndex: 3 },
    { key: 'july_to_september', monthIndex: 6 },
    { key: 'october_to_december', monthIndex: 9 },
  ],
  3: [
    { key: 'january_to_june', monthIndex: 0 },
    { key: 'july_to_december', monthIndex: 6 },
  ],
  4: [{ key: 'january_to_december', monthIndex: 0 }],
};

function getTermPeriodFields(row) {
  const termId = Number(row?.budget_term_id);
  if (TERM_PERIOD_FIELDS[termId]) return TERM_PERIOD_FIELDS[termId];

  return (
    [4, 3, 2, 1].map(id => TERM_PERIOD_FIELDS[id]).find(periods => periods.some(period => period.key in (row || {}))) ||
    TERM_PERIOD_FIELDS[1]
  );
}

function getPeriodAmount(row, periodKey, suffix = '') {
  const suffixedKey = `${periodKey}${suffix}`;
  if (suffixedKey in (row || {})) return Number(row?.[suffixedKey]) || 0;

  // Budget-vs-actual responses normally use `period_budget`, but accept the
  // equivalent `budget_period` form as well for compatibility.
  if (suffix) {
    const prefixedKey = `${suffix.slice(1)}_${periodKey}`;
    if (prefixedKey in (row || {})) return Number(row?.[prefixedKey]) || 0;
  }

  return 0;
}

function addPeriodAmounts(target, row, suffix = '') {
  getTermPeriodFields(row).forEach(period => {
    target[period.monthIndex] += getPeriodAmount(row, period.key, suffix);
  });
}

export function collapseMonthsToTerm(months, budgetTermId) {
  const size = MONTHS_PER_TERM[Number(budgetTermId)] || 1;
  const totals = [];

  for (let index = 0; index < months.length; index += size) {
    totals.push(months.slice(index, index + size).reduce((sum, amount) => sum + amount, 0));
  }

  return totals;
}

export function collapseBudgetVsActualMonths(months, budgetTermId) {
  const size = MONTHS_PER_TERM[Number(budgetTermId)] || 1;
  const periods = [];

  for (let index = 0; index < months.length; index += size) {
    periods.push(
      months.slice(index, index + size).reduce(
        (total, month) => ({
          budget: total.budget + month.budget,
          actual: total.actual + month.actual,
        }),
        { budget: 0, actual: 0 }
      )
    );
  }

  return periods;
}

// account_main_name is the only grouping the master data gives us, so the
// budget sections are derived from it.
export function categorizeAccount(account) {
  const main = (account?.account_main_name || '').toLowerCase();
  if (main.includes('income')) return 'income';
  if (main.includes('expense')) return 'expense';
  if (main.includes('asset')) return 'asset';
  if (main.includes('liability')) return 'liability';
  if (main.includes('equity')) return 'equity';
  return 'other';
}

// <root><a><account_id/><term_id/><amount/></a>...</root>
export function buildBudgetXml(lines) {
  const body = lines
    .map(
      line =>
        `<a><account_id>${line.account_id}</account_id>` + `<term_id>${line.term_id}</term_id>` + `<amount>${line.amount}</amount></a>`
    )
    .join('');

  return `<root>${body}</root>`;
}

// the endpoints are inconsistent: rows may come back as an array, as a bare
// object when there is only one, or wrapped in a key such as budget_info.
export function normalizeBudgetList(serverData) {
  if (!serverData) return [];
  if (Array.isArray(serverData)) return serverData.flat();
  if (typeof serverData !== 'object') return [];

  if ('budget_ref_no' in serverData) return [serverData];
  if (Array.isArray(serverData.budget_info)) return serverData.budget_info.flat();

  const wrapped = Object.values(serverData).find(value => Array.isArray(value));
  return wrapped ? wrapped.flat() : [];
}

export function normalizeBudgetListPayload(serverData) {
  const responseData =
    Array.isArray(serverData) && serverData.length === 1 && typeof serverData[0] === 'object' && !('budget_ref_no' in serverData[0])
      ? serverData[0]
      : serverData;

  return {
    budget_info: normalizeBudgetList(responseData),
    financial_year: Array.isArray(responseData?.financial_year) ? responseData.financial_year : [],
    budget_term: Array.isArray(responseData?.budget_term) ? responseData.budget_term : [],
  };
}

export function normalizeBudgetDetails(serverData) {
  if (!serverData) return [];
  if (Array.isArray(serverData)) return serverData.flat();
  if (typeof serverData !== 'object') return [];

  if (Array.isArray(serverData.budget_info)) return serverData.budget_info.flat();
  if ('account_id' in serverData) return [serverData];

  const wrapped = Object.values(serverData).find(value => Array.isArray(value));
  return wrapped ? wrapped.flat() : [];
}

const clean = value => (typeof value === 'string' ? value.trim() : (value ?? ''));

// The endpoint returns one row per account/period. Period-field names vary by
// budget term, so normalize them into canonical month slots before aggregating.
export function buildAccountRows(rows) {
  const accounts = new Map();

  rows.forEach(row => {
    const id = row?.account_id;
    if (id === undefined || id === null) return;

    let account = accounts.get(id);
    if (!account) {
      account = {
        account_id: id,
        account_name: clean(row?.account_name),
        account_type_name: clean(row?.account_type_name),
        account_main_name: clean(row?.account_main_name) || 'Other',
        parent_account_name: clean(row?.parent_account_name),
        months: BUDGET_MONTHS.map(() => 0),
        total: 0,
      };
      accounts.set(id, account);
    }

    addPeriodAmounts(account.months, row);
  });

  accounts.forEach(account => {
    account.total = account.months.reduce((sum, amount) => sum + amount, 0);
  });

  return [...accounts.values()].sort(
    (a, b) => a.account_main_name.localeCompare(b.account_main_name) || a.account_name.localeCompare(b.account_name)
  );
}

// The API emits one (or more) rows for each account/period. Aggregate each
// budget/actual pair after normalizing the term-specific period fields.
export function buildBudgetVsActualGroups(rows) {
  const accounts = new Map();

  rows.forEach(row => {
    const id = row?.account_id;
    if (id === undefined || id === null) return;

    let account = accounts.get(id);
    if (!account) {
      account = {
        account_id: id,
        account_name: clean(row?.account_name),
        account_type_name: clean(row?.account_type_name),
        account_main_name: clean(row?.account_main_name) || 'Other',
        parent_account_name: clean(row?.parent_account_name),
        months: BUDGET_MONTHS.map(month => ({
          key: month.key,
          label: month.label,
          budget: 0,
          actual: 0,
        })),
      };
      accounts.set(id, account);
    }

    const budget = account.months.map(month => month.budget);
    const actual = account.months.map(month => month.actual);
    addPeriodAmounts(budget, row, '_budget');
    addPeriodAmounts(actual, row, '_actual');
    account.months.forEach((month, index) => {
      month.budget = budget[index];
      month.actual = actual[index];
    });
  });

  const groups = new Map();
  [...accounts.values()]
    .sort((a, b) => a.account_main_name.localeCompare(b.account_main_name) || a.account_name.localeCompare(b.account_name))
    .forEach(account => {
      const months = account.months.filter(month => month.budget !== 0 || month.actual !== 0);

      let group = groups.get(account.account_main_name);
      if (!group) {
        group = { name: account.account_main_name, accounts: [], budget: 0, actual: 0 };
        groups.set(account.account_main_name, group);
      }

      const budget = account.months.reduce((sum, month) => sum + month.budget, 0);
      const actual = account.months.reduce((sum, month) => sum + month.actual, 0);
      group.accounts.push({ ...account, canonicalMonths: account.months, months, budget, actual });
      group.budget += budget;
      group.actual += actual;
    });

  return [...groups.values()];
}

// Balance-sheet rows are returned with PascalCase field names and represent a
// point-in-time value. Keep the API hierarchy intact so the report can show
// account-type, account-main, and balance-sheet-group subtotals.
export function normalizeBalanceSheetRows(serverData) {
  if (!serverData) return [];

  const isBalanceSheetRow = row => row && typeof row === 'object' && ('Account_Id' in row || 'account_id' in row);

  if (Array.isArray(serverData)) return serverData.flat(Infinity).filter(isBalanceSheetRow);
  if (isBalanceSheetRow(serverData)) return [serverData];
  if (typeof serverData !== 'object') return [];

  const wrappedRows = Object.values(serverData).find(value => Array.isArray(value));
  return wrappedRows ? normalizeBalanceSheetRows(wrappedRows) : [];
}

const balanceSheetValue = (row, pascalKey, camelKey) => row?.[pascalKey] ?? row?.[camelKey];
const balanceSheetName = (row, pascalKey, camelKey, fallback) =>
  clean(balanceSheetValue(row, pascalKey, camelKey)) || fallback;
const balanceSheetId = (row, pascalKey, camelKey, fallback) => balanceSheetValue(row, pascalKey, camelKey) ?? fallback;

const balanceSheetGroupRank = name => {
  const normalized = name.toLowerCase();
  if (normalized.includes('asset')) return 0;
  if (normalized.includes('liabilit')) return 1;
  if (normalized.includes('equit')) return 2;
  return 3;
};

const accountMainRank = name => {
  const normalized = name.toLowerCase();
  if (normalized.includes('current')) return 0;
  if (normalized.includes('non current') || normalized.includes('long term')) return 1;
  if (normalized.includes('fixed')) return 2;
  if (normalized.includes('other')) return 3;
  return 4;
};

const byName = (left, right) => left.name.localeCompare(right.name);

export function buildBalanceSheetGroups(rows) {
  const groups = new Map();

  normalizeBalanceSheetRows(rows).forEach(row => {
    const groupName = balanceSheetName(row, 'Balance_Sheet_Group_Name', 'balance_sheet_group_name', 'Other');
    const groupId = balanceSheetId(row, 'Balance_Sheet_Group_Id', 'balance_sheet_group_id', groupName);
    const mainName = balanceSheetName(row, 'Account_Main_Name', 'account_main_name', 'Other');
    const mainId = balanceSheetId(row, 'Account_Main_Id', 'account_main_id', mainName);
    const typeName = balanceSheetName(row, 'Account_Type_Name', 'account_type_name', 'Other');
    const typeId = balanceSheetId(row, 'Account_Type_Id', 'account_type_id', typeName);
    const accountName = balanceSheetName(row, 'Account_Name', 'account_name', 'Unnamed account');
    const accountId = balanceSheetId(row, 'Account_Id', 'account_id', accountName);
    const amount = Number(balanceSheetValue(row, 'Amount', 'amount')) || 0;

    const groupKey = `${groupId}-${groupName}`;
    if (!groups.has(groupKey)) groups.set(groupKey, { id: groupId, name: groupName, mains: [], total: 0, mainMap: new Map() });
    const group = groups.get(groupKey);

    const mainKey = `${mainId}-${mainName}`;
    if (!group.mainMap.has(mainKey)) group.mainMap.set(mainKey, { id: mainId, name: mainName, types: [], total: 0, typeMap: new Map() });
    const main = group.mainMap.get(mainKey);

    const typeKey = `${typeId}-${typeName}`;
    if (!main.typeMap.has(typeKey)) main.typeMap.set(typeKey, { id: typeId, name: typeName, accounts: [], total: 0, accountMap: new Map() });
    const type = main.typeMap.get(typeKey);

    const accountKey = `${accountId}-${accountName}`;
    if (!type.accountMap.has(accountKey)) type.accountMap.set(accountKey, { id: accountId, name: accountName, amount: 0 });
    type.accountMap.get(accountKey).amount += amount;
  });

  return [...groups.values()]
    .map(group => {
      group.mains = [...group.mainMap.values()]
        .map(main => {
          main.types = [...main.typeMap.values()]
            .map(type => {
              type.accounts = [...type.accountMap.values()].sort(byName);
              type.total = type.accounts.reduce((sum, account) => sum + account.amount, 0);
              delete type.accountMap;
              return type;
            })
            .sort(byName);
          main.total = main.types.reduce((sum, type) => sum + type.total, 0);
          delete main.typeMap;
          return main;
        })
        .sort((left, right) => accountMainRank(left.name) - accountMainRank(right.name) || byName(left, right));
      group.total = group.mains.reduce((sum, main) => sum + main.total, 0);
      delete group.mainMap;
      return group;
    })
    .sort((left, right) => balanceSheetGroupRank(left.name) - balanceSheetGroupRank(right.name) || byName(left, right));
}
export function groupAccountsByMain(accounts) {
  const groups = new Map();

  accounts.forEach(account => {
    let group = groups.get(account.account_main_name);
    if (!group) {
      group = {
        name: account.account_main_name,
        accounts: [],
        months: BUDGET_MONTHS.map(() => 0),
        total: 0,
      };
      groups.set(account.account_main_name, group);
    }

    group.accounts.push(account);
    account.months.forEach((amount, index) => {
      group.months[index] += amount;
    });
    group.total += account.total;
  });

  return [...groups.values()];
}

export function sumMonths(items) {
  return items.reduce(
    (totals, item) => totals.map((amount, index) => amount + item.months[index]),
    BUDGET_MONTHS.map(() => 0)
  );
}
